/**
 * InvitePane — the V2 rail's Invite tab (plan `2026-10-01-001-rail-glaze-
 * parity-investigation.md` §8, dispatch unit U13). Restyles `EQ/
 * CustomInvitePanel.tsx`'s body onto Glaze, following `clayground`'s
 * `rail/plays/InvitePane.tsx` prototype for layout shape — a flat column,
 * no header, no section labels, no close button.
 *
 * All data/logic is `useCustomInviteForm()` (U6, extracted verbatim out of
 * `CustomInvitePanel.tsx`), held by `CustomInviteFormProvider` above the dock
 * so the draft survives dock close and tab switches — this component owns no
 * query/mutation/state of its own. `RailDock` (U14) owns the tab label, the close affordance, and
 * the "Read-only while impersonating" line; none of those live here.
 *
 * Kept from V1, copy and conditions unchanged (investigation doc §8 "Keep"
 * list): real lookup with found/not-found/error states, name autofill,
 * Owner/ClayDR line, already-invited warning, campaign history table,
 * inline send error + "Send as yourself instead", read-only gating, Change
 * campaign. The campaign step reuses Terra's own `PlayPicker` (richer than
 * the prototype's plain `SelectField` — search/filtering the prototype's
 * stub never modeled), per the investigation doc's baked-in decision.
 */
import { Button } from '@clay-run/glaze/button';
import { InputField } from '@clay-run/glaze/input-field';
import { SelectField } from '@clay-run/glaze/select-field';
import { TextareaField } from '@clay-run/glaze/textarea-field';
import { Strong, Text } from '@clay-run/glaze/text';
import { Alert, AlertHeader, AlertDescription } from '@clay-run/glaze/alert';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { PlayPicker } from '../../EnrollQueue/PlayPicker';
import { CampaignHistoryTable } from '../../EnrollQueue/CampaignHistoryTable';
import { EMPTY_GONG_FLOWS, READ_ONLY_MESSAGE } from '../../EnrollQueue/useCustomInviteForm';
import { useCustomInviteFormContext } from '../../EnrollQueue/CustomInviteFormProvider';
import { AUTOFILL_IGNORE_PROPS } from '../../../../lib/autofill';

export function InvitePane() {
  const {
    readOnly,
    eventInviteType,
    eventInvitePlays,
    eventInviteTypes,
    senders,
    selfSenderAvailable,
    email,
    name,
    nameSource,
    lookedUpEmail,
    lookupCurrent,
    normalizedEmail,
    emailValid,
    selectedPlay,
    setSelectedPlay,
    sender,
    setSender,
    subject,
    setSubject,
    emailCopy,
    setEmailCopy,
    lookup,
    inviteStatus,
    sendMutation,
    handleEmailChange,
    handleNameChange,
    handleLookup,
    handleEmailKeyDown,
    handleSend,
    handleRetryAsSelf,
    sendDisabled,
  } = useCustomInviteFormContext();

  return (
    <div className="flex flex-col gap-3">
      <Strong className="text-xs">Invite a contact</Strong>
      <Text size="xs" variant="tertiary">
        Invite someone by email — no Salesforce contact needed
      </Text>

      {eventInviteType === undefined ? (
        <Text size="sm" variant="secondary">
          Event invites aren&apos;t available right now — there is no active Event Invite play type.
        </Text>
      ) : (
        <>
          {/* Who — email + lookup share one row, full name below. */}
          <div className="flex items-end gap-2">
            <InputField
              {...AUTOFILL_IGNORE_PROPS}
              label="Email"
              type="email"
              placeholder="name@company.com"
              size="sm"
              aria-label="Email to invite"
              className="flex-1"
              value={email}
              onChange={handleEmailChange}
              onKeyDown={handleEmailKeyDown}
              disabled={readOnly}
            />
            <Button
              variant="secondary"
              size="sm"
              aria-label="Look up contact"
              disabled={!emailValid || lookup.isFetching || readOnly}
              onClick={handleLookup}
            >
              Look up
            </Button>
          </div>

          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            label="Full name"
            placeholder="First Last"
            size="sm"
            aria-label="Full name"
            value={name}
            onChange={handleNameChange}
            disabled={readOnly}
          />
          {nameSource === 'lookup' && (
            <Text size="xs" variant="tertiary">
              Auto-filled from contact lookup
            </Text>
          )}
          {/* Live region (ported verbatim from CustomInvitePanel.tsx's G5
              fix): the wrapper mounts with the "Looking up contact…" state
              at click time, so the later swap to found/not-found/error is a
              content change INSIDE an existing live region. */}
          {lookedUpEmail !== null && lookupCurrent && (
            <div aria-live="polite">
              {lookup.isFetching ? (
                <div className="flex items-center gap-1.5">
                  <LoadingSpinner size="sm" />
                  <Text size="xs" variant="tertiary">
                    Looking up contact…
                  </Text>
                </div>
              ) : lookup.isError ? (
                <Alert status="error">{lookup.error?.message ?? 'Lookup failed'}</Alert>
              ) : lookup.data?.contact ? (
                <>
                  <Text size="xs" variant="success">
                    {`Contact found — ${lookup.data.campaigns.length} campaign${lookup.data.campaigns.length === 1 ? '' : 's'}`}
                    {lookup.data.contact.account_name ? ` · ${lookup.data.contact.account_name}` : ''}
                  </Text>
                  <Text size="xs" variant="tertiary">
                    {`Owner: ${lookup.data.contact.owner_email ?? 'none assigned'} · ClayDR: ${lookup.data.contact.claydr_email ?? 'none assigned'}`}
                  </Text>
                </>
              ) : lookup.data ? (
                <Text size="xs" variant="tertiary">
                  No contact found — will be created on invite
                </Text>
              ) : null}
            </div>
          )}

          {/* Campaign — Terra's real PlayPicker, not a plain SelectField
              (investigation doc §8 decision). */}
          {selectedPlay === null ? (
            <PlayPicker
              plays={eventInvitePlays}
              activePlayTypes={eventInviteTypes}
              gongFlowsSuggested={EMPTY_GONG_FLOWS}
              onAssign={(play) => setSelectedPlay(play)}
              ariaLabelPrefix="Invite to"
              contactHasEmail={true}
              warmIntroAvailable={false}
              disabled={readOnly}
              disabledReason={readOnly ? READ_ONLY_MESSAGE : undefined}
            />
          ) : (
            <div className="flex items-center justify-between gap-2 rounded-md border border-secondary px-2.5 py-1.5">
              <Text size="sm">{selectedPlay.name}</Text>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Change campaign"
                onClick={() => setSelectedPlay(null)}
                disabled={sendMutation.isPending}
              >
                Change
              </Button>
            </div>
          )}

          {/* Subject/Body — only once a campaign is picked (nothing to seed before that). */}
          {selectedPlay !== null && (
            <>
              <InputField
                {...AUTOFILL_IGNORE_PROPS}
                label="Subject"
                aria-label="Invite subject"
                size="sm"
                value={subject}
                onValueChange={(value: string) => setSubject(value)}
                disabled={readOnly}
              />
              <TextareaField
                {...AUTOFILL_IGNORE_PROPS}
                label="Body"
                aria-label="Invite body"
                size="sm"
                rows={4}
                value={emailCopy}
                onValueChange={(value: string) => setEmailCopy(value)}
                disabled={readOnly}
              />
            </>
          )}

          <SelectField
            label="Sender"
            aria-label="Sender"
            items={senders}
            value={sender}
            onValueChange={(v: string | null) => setSender(v)}
            placeholder="Select sender…"
            size="sm"
            disabled={readOnly || senders.length === 0 || sendMutation.isPending}
          />

          {/* Dedup warning — warn-only, never blocks Send. */}
          {selectedPlay !== null && inviteStatus.data?.already_invited && (
            <Alert status="warning">
              <AlertHeader>Already invited</AlertHeader>
              <AlertDescription>
                {normalizedEmail} was already invited to {selectedPlay.name}
                {inviteStatus.data.last_invited_at
                  ? ` on ${new Date(inviteStatus.data.last_invited_at).toLocaleDateString()}`
                  : ''}
                {' ('}
                {inviteStatus.data.sources.includes('terra_invite') ? 'sent from Terra' : 'Salesforce campaign member'}
                {'). Sending again will send another invite.'}
              </AlertDescription>
            </Alert>
          )}
          {selectedPlay !== null && inviteStatus.isError && (
            <Text size="xs" variant="tertiary">
              Couldn&apos;t check invite history
            </Text>
          )}

          {/* Campaign history — independent of the campaign-scoped
              "Already invited" warning above. */}
          {lookup.data && lookupCurrent && (
            <CampaignHistoryTable
              rows={lookup.data.campaigns}
              selectedCampaignId={selectedPlay?.play_instance_ref ?? null}
            />
          )}

          {/* The server's own message, never a raw status code. */}
          {sendMutation.isError && (
            <Alert status="error" error={sendMutation.error}>
              {sendMutation.error.message}
            </Alert>
          )}
          {sendMutation.isError &&
            sendMutation.error?.code === 'sender_unresolved' &&
            selfSenderAvailable && (
              <Button variant="secondary" size="sm" onClick={handleRetryAsSelf} disabled={sendMutation.isPending}>
                Send as yourself instead
              </Button>
            )}

          <div className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSend}
              disabled={sendDisabled}
              title={readOnly ? READ_ONLY_MESSAGE : undefined}
            >
              {sendMutation.isPending ? 'Sending…' : 'Send invite'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
